import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { useFormik } from 'formik';
import * as SalesCostApi from '../../lib/api/salesCosts';
import * as yup from 'yup';
import * as dayjs from 'dayjs';

// grid
import { useGridApiRef, GridActionsCellItem } from '@mui/x-data-grid';
// icons
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import Button from '@mui/material/Button';

// redux
import {
  listSalesCosts,
  setBaseFilters,
} from '../../modules/salesCostModule/salesCosts';

// components
import BreadcrumbsContainer from '../common/breadcurmbs/BreadcrumbsContainer';
import SalesCostList from '../../components/sales-cost-manage/SalesCostList';
import { useBoolean } from '../../hooks/useBoolean';
import { fDate } from '../../utils/calcDatetime';
import { getCompanies } from '../../modules/auth';
import { SalesCostListBox } from '../../components/sales-cost-manage/SalesCostListBox';
import SalesCostNewEditFormDialog from './SalesCostNewEditFormDialog';
import SalesCostUploadModalContainer from './SalesCostUploadModalContainer';
import AskDialog from '../../components/common/dialog/AskDialog';

const SalesCostListContainer = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const { companies, salesCosts, defaultFilters, error, loading } = useSelector(
    ({ auth, salesCostModule, loading }) => ({
      companies: auth.companies,
      salesCosts: salesCostModule.salesCosts.salesCosts,
      defaultFilters: salesCostModule.salesCosts.defaultFilters,
      error: salesCostModule.salesCosts.error,
      loading: loading['salesCosts/LIST_SALES_COSTS'],
    }),
  );

  const [tableData, setTableData] = useState([]);

  const makeFilters = (filters) => {
    const startDate =
      filters.searchStrtDt !== null && filters.searchStrtDt !== ''
        ? dayjs(filters.searchStrtDt).format('YYYY-MM-DD')
        : '';
    const endDate =
      filters.searchEndDt !== null && filters.searchEndDt !== ''
        ? dayjs(filters.searchEndDt).format('YYYY-MM-DD')
        : '';

    let searchData = {
      companyCd: filters.companyCd,
      [filters.searchKeyword]: filters.searchWords,
      strtDt: startDate,
      endDt: endDate,
    };

    return searchData;
  };

  useEffect(() => {
    dispatch(listSalesCosts(makeFilters(defaultFilters)));
  }, [defaultFilters]);

  useEffect(() => {
    dispatch(getCompanies());
  }, []);

  useEffect(() => {
    if (salesCosts) {
      setTableData(salesCosts);
    }
  }, [salesCosts]);

  const showUpload = useBoolean();
  const isEdit = useBoolean();
  const showDialog = useBoolean();
  const [selectedRow, setSelectedRow] = useState(null);

  // upload
  const handleUpload = useCallback(() => {
    showUpload.onTrue();
  }, [showUpload]);

  // add
  const handleAddCost = useCallback(() => {
    isEdit.onFalse();
    showDialog.onTrue();
  }, [isEdit, showDialog]);

  // Edit
  const editCost = useCallback(
    (row) => () => {
      //  console.log(row, 'row');
      setSelectedRow(row);
      isEdit.onTrue();
      showDialog.onTrue();
    },
    [isEdit, showDialog],
  );

  // delete
  const delConfirm = useBoolean();
  const [delRow, setDelRow] = useState(null);
  const deleteCost = useCallback(
    (row) => () => {
      setDelRow(row);
      delConfirm.onTrue();
    },
    [delConfirm],
  );

  const handleDeleteCost = useCallback(async () => {
    try {
      const res = await SalesCostApi.deleteSalesCost(delRow?.id);

      if (res.data.result === 'success') {
        setDelRow(null);
        delConfirm.onFalse();
        enqueueSnackbar('판매관리비 삭제에 성공하였습니다');
        dispatch(makeFilters(listSalesCosts(defaultFilters)));
      }
    } catch (err) {
      console.log(err);
      delConfirm.onFalse();
      enqueueSnackbar('판매관리비 삭제에 실패하였습니다', { variant: 'error' });
    }
  }, [defaultFilters, delConfirm, delRow?.id, dispatch, enqueueSnackbar]);

  const apiRef = useGridApiRef();
  const initialState = {
    sorting: {
      sortModel: [{ field: 'scDt', sort: 'desc' }],
    },
  };

  const columns = useMemo(
    () => [
      { field: 'id', hide: true, identity: true },
      { field: 'scUuid', hide: true, identity: true },
      { field: 'scDt', headerName: '일자', minWidth: 150, type: 'string' },
      {
        field: 'scLarge',
        headerName: '계정과목(대)',
        minWidth: 150,
        type: 'string',
      },
      {
        field: 'scSmall',
        headerName: '계정과목(소)',
        minWidth: 150,
        type: 'string',
      },
      { field: 'scWh', headerName: '사용처', minWidth: 200, type: 'string' },
      { field: 'scItem', headerName: '품목', type: 'string' },
      {
        field: 'scTotal',
        headerName: '금액',
        type: 'number',
        minWidth: 150,
        flex: 0.7,

        valueFormatter: (params) => {
          const valueFormatted = Number(params).toLocaleString('ko-KR');
          return `₩ ${valueFormatted}`;
        },
      },
      { field: 'isTax', headerName: '부가세 여부', type: 'string' },
      { field: 'isBill', headerName: '계산서 여부', type: 'string' },

      { field: 'companyNm', headerName: '회사', type: 'string' },

      { field: 'usedDisplayNm', headerName: '대상', type: 'string' },
      {
        field: 'cngNm',
        headerName: '수정자',
        description: '등록/수정자',
        type: 'string',
      },
      {
        field: 'cngDt',
        headerName: '수정일',
        minWidth: 120,
        flex: 1,
        type: 'string',
        editable: false,
        renderCell: (params) => fDate(params.value),
      },

      {
        field: 'actions',
        type: 'actions',
        width: 80,
        getActions: (params) => [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            onClick={editCost(params.row)}
            //showInMenu
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            onClick={deleteCost(params.row)}
          />,
        ],
      },
    ],
    [deleteCost, editCost],
  );

  const initialValues = {
    companyCd: defaultFilters.companyCd,
    searchKeyword: defaultFilters.searchKeyword,
    searchWords: defaultFilters.searchWords,
    searchStrtDt: defaultFilters.searchStrtDt,
    searchEndDt: defaultFilters.searchEndDt,
  };

  const validationSchema = yup.object({
    searchKeyword: yup.string('').nullable(),
    searchWords: yup.string('').trim().nullable(),
    searchStrtDt: yup.date().nullable().default(undefined),
    searchEndDt: yup
      .date()
      .min(yup.ref('searchStrtDt'), '등록 종료일을 확인하세요')
      .when('searchStrtDt', (searchStrtDt, schema) => {
        if (searchStrtDt) return schema.required('등록 종료일을 입력하세요');
      })
      .nullable()
      .default(undefined),
  });

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(true);

      setTimeout(() => {
        const searchData = {
          companyCd: values.companyCd,
          searchKeyword: values.searchKeyword,
          searchWords: values.searchWords,
          searchStrtDt: values.searchStrtDt,
          searchEndDt: values.searchEndDt,
        };
        dispatch(setBaseFilters(searchData));

        setSubmitting(false);
      }, 1000);
    },
  });

  const refreshAndClose = () => {
    dispatch(listSalesCosts(makeFilters(defaultFilters)));
    showDialog.onFalse();
    setSelectedRow(null);
  };

  const refreshOnClose = () => {
    showUpload.onFalse();
    dispatch(listSalesCosts(makeFilters(defaultFilters)));
  };
  return (
    <>
      <BreadcrumbsContainer title="판관비 관리/판매관리비/리스트" icon="cost" />
      <SalesCostListBox
        formik={formik}
        companyOptions={companies}
        handleAdd={handleAddCost}
        onUpload={handleUpload}
      />
      <SalesCostList
        apiRef={apiRef}
        columns={columns}
        rows={tableData}
        error={error}
        loading={loading}
        initialState={initialState}
        columnVisibilityModel={{
          id: false,
          scUuid: false,
        }}
      />
      <SalesCostNewEditFormDialog
        companyOptions={companies}
        currentCost={selectedRow}
        open={showDialog.value}
        isEdit={isEdit.value}
        onClose={() => {
          showDialog.onFalse();
          setSelectedRow(null);
        }}
        refreshAndClose={refreshAndClose}
      />
      <AskDialog
        visible={delConfirm.value}
        title="계정과목 삭제"
        description={`${delRow?.scLarge}/${delRow?.scSmall}을 삭제하시겠습니까?`}
        confirmText="삭제"
        onConfirm={handleDeleteCost}
        onCancle={() => {
          delConfirm.onFalse();
          setDelRow(null);
        }}
      />

      <SalesCostUploadModalContainer
        title="계정과목 엑셀 업로드"
        open={showUpload.value}
        onClose={() => {
          showUpload.onFalse();
        }}
        refreshOnClose={refreshOnClose}
      />
    </>
  );
};

export default SalesCostListContainer;
